import { Property } from '../types/property';
import { enhancedFetch } from '../utils/api-helpers';
import { filterZestimateDescriptions } from '../utils';
import { cacheSaleProperties } from '../utils/centralized-cache';
import { matchesListingStatus, ListingStatus } from '../utils/status-gating';

/**
 * Extract number of bedrooms from formattedChip.quickFacts
 */
function extractBedroomsFromFormattedChip(formattedChip: any): number {
  if (!formattedChip?.quickFacts) return 0;
  
  const bedsFact = formattedChip.quickFacts.find((fact: any) => 
    fact.elementType === 'beds'
  );
  
  if (bedsFact?.value?.fullValue) {
    return parseInt(bedsFact.value.fullValue, 10);
  }
  
  return 0;
}

/**
 * Extract number of bathrooms from formattedChip.quickFacts
 */
function extractBathroomsFromFormattedChip(formattedChip: any): number {
  if (!formattedChip?.quickFacts) return 0;
  
  const bathsFact = formattedChip.quickFacts.find((fact: any) => 
    fact.elementType === 'baths'
  );
  
  if (bathsFact?.value?.fullValue) {
    return parseFloat(bathsFact.value.fullValue);
  }
  
  return 0;
}


export interface AddressPropertySearchParams {
  // Address parameter (resolved from auto-complete)
  address: string;
  
  // Address metadata from auto-complete response
  addressMetadata?: {
    streetNumber?: string;
    streetName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  
  // Optional cookie for Zillow API
  cookie?: string;
  
  // Expected listing status for filtering (optional)
  expectedStatus?: ListingStatus;
}

/**
 * Step 1: Auto-complete API (Resolver) to get standardized address
 * @param userInput - Raw user input (address, street, etc.)
 * @returns Promise<{address: string, metadata: any}> - Standardized address and metadata
 */
async function getAddressFromAutoComplete(userInput: string): Promise<{address: string, metadata: any}> {
  try {
    
    const autoCompleteUrl = '/api/properties/auto-complete';
    const params = new URLSearchParams();
    params.append('query', userInput);

    const fullUrl = `${autoCompleteUrl}?${params.toString()}`;

    const response = await enhancedFetch(
      fullUrl,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    
    // Filter for address-type suggestions
    if (data.data && Array.isArray(data.data)) {
      const addressSuggestions = data.data
        .filter((item: any) => item.resultType === "Address" && item.display)
        .map((item: any) => ({
          display: item.display,
          resultType: item.resultType,
          metaData: item.metaData
        }));
      
      if (addressSuggestions.length > 0) {
        const selectedAddress = addressSuggestions[0];
        return {
          address: selectedAddress.display,
          metadata: selectedAddress.metaData
        };
      }
    }

    // If no suggestions found, use the original input
    return {
      address: userInput,
      metadata: null
    };
  } catch (error) {
    // Fallback to original input if auto-complete fails
    return {
      address: userInput,
      metadata: null
    };
  }
}

/**
 * Step 2: Address API (properties/search-address) for property data
 * @param params - AddressPropertySearchParams object
 * @returns Promise<Property> - Single property details
 */
async function searchPropertyWithAddress(params: AddressPropertySearchParams): Promise<Property> {
  try {
    
    const searchUrl = '/api/properties/search-address';
    const searchParams = new URLSearchParams();
    
    // Required parameter - address
    searchParams.append('address', params.address);
    
    // Optional cookie
    if (params.cookie) {
      searchParams.append('cookie', params.cookie);
    }

    const fullUrl = `${searchUrl}?${searchParams.toString()}`;

    const response = await enhancedFetch(
      fullUrl,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    
    // Process the property data from address API
    if (data.data) {
      const propertyData = data.data;
      
      // Status gating: filter by expected status if specified
      if (params.expectedStatus) {
        if (!matchesListingStatus(propertyData, params.expectedStatus)) {
          const upstreamStatus = (propertyData.homeStatus || propertyData.status || propertyData.listing?.marketingStatus || '').toString();
          throw new Error(`Property status '${upstreamStatus}' does not match expected status '${params.expectedStatus}'`);
        }
      }
      
      // Transform address API property data to our Property type
      // Extract lot size and unit robustly (address-based payloads vary)
      let extractedLotSize: number = 0;
      let extractedLotUnit: string | undefined = undefined;
      
      if (propertyData.resoFacts?.lotSize && typeof propertyData.resoFacts.lotSize === 'string') {
        // Parse formats like "7,013 sqft" or "0.33 acres"
        const sizeMatch = propertyData.resoFacts.lotSize.match(/([\d,.]+)/);
        if (sizeMatch) {
          const numeric = parseFloat(sizeMatch[1].replace(/,/g, ''));
          if (!Number.isNaN(numeric)) extractedLotSize = numeric;
        }
        const unitMatch = propertyData.resoFacts.lotSize.match(/(acre|acres|sqft|square\s*feet|squarefeet)/i);
        if (unitMatch) {
          extractedLotUnit = unitMatch[1]
            .toLowerCase()
            .replace(/\s+/g, '') === 'acre' || unitMatch[1].toLowerCase() === 'acres'
            ? 'Acres'
            : 'Square Feet';
        }
      }
      
      if (!extractedLotSize) {
        extractedLotSize = propertyData.lotAreaValue ?? (propertyData.lotSizeWithUnit?.lotSize ?? propertyData.lotArea ?? propertyData.lotSize ?? 0);
      }
      if (!extractedLotUnit) {
        extractedLotUnit = propertyData.lotAreaUnit || propertyData.lotSizeWithUnit?.lotSizeUnit || undefined;
        if (!extractedLotUnit && extractedLotSize) {
          // Infer a unit if missing (heuristic)
          extractedLotUnit = extractedLotSize < 10 ? 'Acres' : 'Square Feet';
        }
      }
      
      const property: Property = {
        id: propertyData.zpid?.toString() || '',
        title: '', // Don't store titles for Buy properties - this prevents duplicate addresses
        price: propertyData.price || 0,
        address: params.addressMetadata ? `${params.addressMetadata.streetNumber || ''} ${params.addressMetadata.streetName || ''}`.trim() : params.address.split(',')[0]?.trim() || params.address,
        city: params.addressMetadata?.city || '',
        state: params.addressMetadata?.state || '',
        zipCode: params.addressMetadata?.zipCode || '',
        type: propertyData.homeType?.toLowerCase() || 'singleFamily',
        imageUrl: propertyData.imageLink || '',
        description: filterZestimateDescriptions(propertyData.description || ''),
        location: {
          lat: propertyData.latitude || 0,
          lon: propertyData.longitude || 0
        },
        photos: (() => {
          // Prioritize high-resolution photos
          if (propertyData.photoUrlsHighRes && Array.isArray(propertyData.photoUrlsHighRes)) {
            return propertyData.photoUrlsHighRes.map((photo: any) => photo.url).filter(Boolean);
          }
          return propertyData.photoUrls?.map((photo: any) => photo.url) || [];
        })(),
        status: propertyData.homeStatus?.toLowerCase() || '',
        details: {
          bedrooms: extractBedroomsFromFormattedChip(propertyData.formattedChip) || 0,
          bathrooms: extractBathroomsFromFormattedChip(propertyData.formattedChip) || 0,
          squareFeet: propertyData.livingAreaValue || 0,
          yearBuilt: propertyData.resoFacts?.yearBuilt || 0,
          lotSize: { amount: extractedLotSize || 0 }
        },
        
        url: propertyData.url || '',
        last_sync: new Date().toISOString(),
        source: 'zillow',
        source_id: propertyData.zpid?.toString() || '',
        lot_area_units: extractedLotUnit || (propertyData.resoFacts?.lotSize ? (propertyData.resoFacts.lotSize.toLowerCase().includes('acre') ? 'Acres' : 'Square Feet') : undefined),
        // Provide a top-level numeric fallback for components that read lot_size directly
        lot_size: extractedLotSize || 0,
      };
      
      // Add additional fields if available (similar to sale-property-search.ts)
      if (propertyData.daysOnZillow) {
        (property as any).daysOnZillow = propertyData.daysOnZillow;
      }
      
      // Check for alternative field names for days on market
      if (propertyData.daysOnMarket) {
        (property as any).daysOnMarket = propertyData.daysOnMarket;
      }
      
      if (propertyData.days_on_market) {
        (property as any).days_on_market = propertyData.days_on_market;
      }
      
      if (propertyData.timeOnZillow) {
        (property as any).timeOnZillow = propertyData.timeOnZillow;
      }
      
      if (propertyData.time_on_zillow) {
        (property as any).time_on_zillow = propertyData.time_on_zillow;
      }
      
      if (propertyData.pricePerSquareFoot) {
        (property as any).pricePerSquareFoot = propertyData.pricePerSquareFoot;
      }
      
      if (propertyData.zestimate) {
        (property as any).zestimate = propertyData.zestimate;
      }
      
      return property;
    } else {
      throw new Error('No property data found in API response');
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Main function to search property by address
 * Step 1: Auto-complete API (Resolver)
 * Step 2: Address API (properties/search-address) for property data
 * Step 3: Cache
 * Step 4: Display results
 * 
 * @param userInput - Raw user input (address, street, etc.)
 * @param searchParams - Additional search parameters
 * @returns Promise<Property> - Single property details
 */
export async function searchPropertyByAddress(
  userInput: string, 
  searchParams: Partial<AddressPropertySearchParams> = {}
): Promise<Property> {
  try {
    
    // Step 1: Auto-complete API (Resolver)
    const { address, metadata } = await getAddressFromAutoComplete(userInput);
    
    // Step 2: Address API (properties/search-address) for property data
    const searchParamsWithAddress: AddressPropertySearchParams = {
      address,
      addressMetadata: metadata,
      ...searchParams
    };
    
    const property = await searchPropertyWithAddress(searchParamsWithAddress);
    
    // Step 3: Cache (non-blocking for instant response)
    // Create search params for dual caching (raw + resolved location)
    const searchParamsForCache = {
      location: address, // Use the resolved address as location
      status: 'forSale',
      page: 0,
      sort: 'relevance'
    };
            cacheSaleProperties([property], userInput, undefined);
    
    // Step 4: Display results
    return property;
  } catch (error) {
    throw error;
  }
}

/**
 * Search property by pre-resolved address
 * @param address - Already resolved address string
 * @param addressMetadata - Address metadata from auto-complete
 * @param searchParams - Additional search parameters
 * @returns Promise<Property> - Single property details
 */
export async function searchPropertyByResolvedAddress(
  address: string,
  addressMetadata?: any,
  searchParams: Partial<AddressPropertySearchParams> = {}
): Promise<Property> {
  try {
    const searchParamsWithAddress: AddressPropertySearchParams = {
      address,
      addressMetadata,
      ...searchParams
    };
    
    const property = await searchPropertyWithAddress(searchParamsWithAddress);
    
    // Cache the property to the database (non-blocking for instant response)
    // Create search params for dual caching (raw + resolved location)
    const searchParamsForCache = {
      location: address, // Use the resolved address as location
      status: 'forSale',
      page: 0,
      sort: 'relevance'
    };
            cacheSaleProperties([property], address, undefined);
    
    return property;
  } catch (error) {
    throw error;
  }
}

/**
 * Get address suggestions from auto-complete
 * @param userInput - Raw user input
 * @returns Promise<Array> - Array of address suggestions
 */
export async function getAddressSuggestions(userInput: string): Promise<any[]> {
  try {
    const autoCompleteUrl = '/api/properties/auto-complete';
    const params = new URLSearchParams();
    params.append('query', userInput);

    const response = await enhancedFetch(
      `${autoCompleteUrl}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    
    // Accept more result types (Address, City, Region, Zip, State)
    const ACCEPTED_TYPES = new Set(['Address', 'City', 'Region', 'Zip', 'State', 'Neighborhood']);
    if (data.data && Array.isArray(data.data)) {
      const suggestions = data.data
        .filter((item: any) => ACCEPTED_TYPES.has(item.resultType) && (item.display || item.name))
        .map((item: any) => ({
          display: item.display || item.name,
          resultType: item.resultType,
          metaData: item.metaData || item.metadata || {},
        }));
      return suggestions;
    }

    return [];
  } catch (error) {
    return [];
  }
} 